/**
 * Контракты режима тренировки переключения раскладок (ТЗ §8.1).
 *
 * Режим стоит особняком среди остальных: он тренирует не клавишу, не слово и
 * не сочетание, а переход между раскладками. В реальной работе переключение
 * стоит времени — пользователь дописывает русскую фразу, вставляет латинское
 * слово и возвращается обратно, — и эта стоимость не измеряется ни одним
 * другим режимом: там текст всегда одноязычный.
 *
 * Поэтому учебная единица — направление перехода, а не символ после него.
 * Задержка перед первым символом нового фрагмента — это не задержка клавиши,
 * а цена смены раскладки, и относить её к букве значило бы обвинять букву в
 * чужой медлительности.
 */

import type {
  HintLevel,
  LanguageCode,
  LayoutId,
  MasteryStat,
  MasteryUnit,
  TrainingMode,
} from '@type2x/shared-contracts';

/** Режим, который ведёт эта функция. */
export const LAYOUT_SWITCH_MODE = 'layout-switch';

/**
 * Режим функции — один из режимов обучения.
 *
 * Проверка на уровне типов: итог сессии уходит на сервер с этим значением, и
 * режим, которого нет в общем перечне, не был бы учтён в разнообразии.
 */
const MODE_IS_TRAINING_MODE: TrainingMode = LAYOUT_SWITCH_MODE;
void MODE_IS_TRAINING_MODE;

/**
 * Раскладки, между которыми идёт переключение.
 *
 * Их ровно две, и обе нужны одновременно: тренировать переход не на чем,
 * если раскладка одна. Порядок здесь не задаёт порядок в упражнении — с
 * какой начать, решает язык профиля.
 */
export const SWITCH_LAYOUTS: Readonly<Record<LanguageCode, LayoutId>> = {
  ru: 'ru-jcuken',
  en: 'en-qwerty',
};

/** Направление перехода: из какой раскладки в какую. */
export interface SwitchDirection {
  readonly id: string;
  readonly from: LanguageCode;
  readonly to: LanguageCode;
}

/** Оба направления: переход туда и обратно — разные движения. */
export const SWITCH_DIRECTIONS: readonly SwitchDirection[] = [
  { id: 'ru>en', from: 'ru', to: 'en' },
  { id: 'en>ru', from: 'en', to: 'ru' },
];

/** Снимок навыка, из которого берётся состояние переходов. */
export interface LayoutSwitchSnapshot {
  /** Язык профиля: с него начинается упражнение. */
  readonly language: LanguageCode;
  readonly stats: Readonly<Record<string, MasteryStat>>;
}

/** Фрагмент упражнения: несколько слов одного языка подряд. */
export interface LayoutSwitchSegment {
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  readonly text: string;
  /** Позиция первого символа фрагмента в тексте занятия, с нуля. */
  readonly start: number;
}

/** Граница между фрагментами: то самое место, где меняется раскладка. */
export interface LayoutSwitchBoundary {
  readonly direction: SwitchDirection;
  /**
   * Позиция первого символа фрагмента после границы.
   *
   * Интервал перед этой позицией и есть стоимость перехода: пользователь
   * ставит пробел, переключает раскладку и только потом нажимает букву.
   */
  readonly position: number;
}

/** Упражнение режима. */
export interface LayoutSwitchExercise {
  readonly seed: string;
  readonly segments: readonly LayoutSwitchSegment[];
  /** Границы смены раскладки в порядке следования. */
  readonly boundaries: readonly LayoutSwitchBoundary[];
  /** Текст для набора: фрагменты, соединённые пробелами. */
  readonly text: string;
}

/** План занятия режима. */
export interface LayoutSwitchPlan {
  readonly mode: typeof LAYOUT_SWITCH_MODE;
  readonly language: LanguageCode;
  /**
   * Раскладка, с которой начинается занятие.
   *
   * Одна раскладка не описывает всё упражнение — в этом и суть режима, — но
   * сессия набора должна с чего-то начать, и начинает с родной для
   * пользователя.
   */
  readonly layoutId: LayoutId;
  /** Учебные единицы переходов, встречающихся в упражнении. */
  readonly units: readonly MasteryUnit[];
  readonly exercise: LayoutSwitchExercise;
  readonly hintLevel: HintLevel;
}

/** Сколько фрагментов в одном упражнении. */
export const SEGMENTS_PER_EXERCISE = 6;

/**
 * Сколько слов в одном фрагменте.
 *
 * Одного слова мало: переход стоит измерять после разгона, иначе задержка
 * перед ним неотличима от задержки старта. Двух хватает, чтобы набрать ритм,
 * и мало, чтобы упражнение не превратилось в обычную тренировку с редкими
 * переключениями.
 */
export const WORDS_PER_SEGMENT = 2;

/**
 * Приставка идентификаторов учебных единиц переходов.
 *
 * Своя приставка, а не `key:` или `pack:`: переход — это не символ, и
 * складывать его статистику с чьей бы то ни было значило бы измерять одно
 * через другое.
 */
export const SWITCH_UNIT_PREFIX = 'switch:';

/**
 * Уровень подсказок режима.
 *
 * Клавиатура скрыта, и это не строгость, а честность: на экране помещается
 * одна раскладка, а упражнение идёт в двух. Показать ЙЦУКЕН над латинским
 * фрагментом значит показать неверные клавиши — хуже, чем не показывать
 * ничего (ТЗ §7.5).
 */
export const HINT_LEVEL: HintLevel = 'keyboard-hidden';
